import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import ProductCard from '../components/product/ProductCard';
import YouTubeModal from '../components/ui/YouTubeModal';
import { fetchProductById, createOrder, fetchReviewsForProduct, createReview, fetchProducts } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

// Helper function to format numbers with spaces
const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const ProductDetail = () => {
    const { id } = useParams();
    const { currentLanguage } = useLanguage();
    const { t } = useTranslation();
    const [product, setProduct] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [otherProducts, setOtherProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0); // For image gallery
    const [orderForm, setOrderForm] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
    });
    const [orderMessage, setOrderMessage] = useState('');
    const [orderError, setOrderError] = useState('');

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewForm, setReviewForm] = useState({
        author_name: '',
        text: '',
        rating: 5,
    });
    const [reviewMessage, setReviewMessage] = useState('');
    const [reviewError, setReviewError] = useState('');
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const loadProductAndReviews = async () => {
            try {
                setLoading(true);
                setError('');
                
                if (!id || isNaN(parseInt(id))) {
                    throw new Error('Mahsulot topilmadi');
                }

                const productData = await fetchProductById(id, currentLanguage);
                if (!productData) {
                    throw new Error('Mahsulot topilmadi');
                }

                if (mounted) {
                    setProduct(productData);
                }

                const [reviewsData, allProducts] = await Promise.all([
                    fetchReviewsForProduct(id).catch(() => []),
                    fetchProducts(currentLanguage).catch(() => [])
                ]);
                
                if (mounted) {
                    setReviews(reviewsData || []);
                    const filteredProducts = (allProducts || []).filter(p => p.id !== productData.id);
                    setOtherProducts(filteredProducts.slice(0, 4));
                }

            } catch (err) {
                console.error('ProductDetail loading error:', err);
                if (mounted && !controller.signal.aborted) {
                    setError(err.message || 'Xatolik yuz berdi');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };
        
        loadProductAndReviews();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [id, currentLanguage]);

    const handleOrderFormChange = (e) => {
        setOrderForm({ ...orderForm, [e.target.name]: e.target.value });
    };

    const handleOrderSubmit = useCallback(async (e) => {
        e.preventDefault();
        setOrderMessage('');
        setOrderError('');
        
        // Validation
        if (!orderForm.first_name.trim() || !orderForm.last_name.trim() || !orderForm.phone_number.trim()) {
            setOrderError('Barcha maydonlarni to\'ldiring');
            return;
        }
        
        try {
            await createOrder({ ...orderForm, product: product.id });
            setOrderMessage('Buyurtma muvaffaqiyatli yuborildi!');
            setOrderForm({ first_name: '', last_name: '', phone_number: '' });
            setTimeout(() => setShowOrderModal(false), 2000);
        } catch (err) {
            console.error('Order submission error:', err);
            setOrderError(err.message || 'Buyurtmani yuborishda xatolik yuz berdi');
        }
    }, [orderForm, product?.id]);

    const handleReviewFormChange = (e) => {
        setReviewForm({ ...reviewForm, [e.target.name]: e.target.value });
    };

    const handleReviewSubmit = useCallback(async (e) => {
        e.preventDefault();
        setReviewMessage('');
        setReviewError('');
        
        // Validation
        if (!reviewForm.author_name.trim() || !reviewForm.text.trim()) {
            setReviewError('Ism va sharh matnini kiriting');
            return;
        }
        
        try {
            await createReview(id, { ...reviewForm, product: product.id });
            setReviewMessage('Sharh muvaffaqiyatli qo\'shildi!');
            setReviewForm({ author_name: '', text: '', rating: 5 });
            setTimeout(() => setShowReviewModal(false), 2000);
            
            // Refresh reviews
            const updatedReviews = await fetchReviewsForProduct(id);
            setReviews(updatedReviews || []);
        } catch (err) {
            console.error('Review submission error:', err);
            setReviewError(err.message || 'Sharh qo\'shishda xatolik yuz berdi');
        }
    }, [reviewForm, id, product?.id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-400 rounded-full animate-spin animation-delay-150"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-red-200">
                    <div className="text-center text-red-600 font-medium">{error}</div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-8">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0H4m16 0l-2-2m-2 2l2 2M6 13l2 2m-2-2l-2 2" />
                            </svg>
                        </div>
                        <div className="text-slate-600 font-medium mb-4">{t('pages.products.noProducts') || 'Mahsulot topilmadi'}</div>
                        <Link 
                            to="/products"
                            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <span className="mr-2">{t('nav.products') || 'Mahsulotlar'}</span>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const originalPrice = product.original_price ? parseFloat(product.original_price) : null;
    const discountPrice = product.discount_price ? parseFloat(product.discount_price) : null;
    const regularPrice = product.price ? parseFloat(product.price) : null;
    const hasDiscount = product.is_discounted && originalPrice && discountPrice;

    const productImages = [product.image1, product.image2].filter(Boolean);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            <Container className="py-8">
                {/* Modern Breadcrumbs */}
                <div className="mb-8">
                    <div className="flex items-center space-x-2 text-sm font-medium">
                        <Link to="/" className="text-slate-600 hover:text-red-600 transition-colors duration-200 hover:underline decoration-2 underline-offset-4">
                            {t('nav.home')}
                        </Link>
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <Link to="/products" className="text-slate-600 hover:text-red-600 transition-colors duration-200 hover:underline decoration-2 underline-offset-4">
                            {t('nav.products')}
                        </Link>
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-slate-900 font-semibold">{product.name}</span>
                    </div>
                </div>

                {/* Main Product Card */}
                <div className="bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                        {/* Enhanced Image Gallery - Smaller */}
                        <div className="lg:col-span-2 relative p-6 lg:p-8 bg-gradient-to-br from-red-50/40 to-white/60">
                            <div className="space-y-4">
                                {/* Main Image - Smaller */}
                                <div className="relative group">
                                    <div className="aspect-square rounded-xl overflow-hidden shadow-xl bg-gradient-to-br from-white to-slate-50">
                                        <img 
                                            src={productImages[selectedImage] || product.image1}
                                            alt={product.name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        
                                        {/* YouTube Video Play Button - Medium for Detail Page */}
                                        {product.youtube_url && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <button
                                                    onClick={() => setIsVideoModalOpen(true)}
                                                    className="group/video flex items-center justify-center w-16 h-16 bg-black/70 hover:bg-red-600 text-white rounded-full transition-all duration-300 transform hover:scale-110 shadow-2xl backdrop-blur-sm border-3 border-white/20"
                                                >
                                                    <svg className="w-6 h-6 ml-1 transition-transform duration-300 group-hover/video:scale-110" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M8 5v14l11-7z"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Image overlay effects */}
                                    <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
                                </div>

                                {/* Image Thumbnails - Smaller */}
                                {productImages.length > 1 && (
                                    <div className="flex space-x-3 justify-center">
                                        {productImages.map((image, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setSelectedImage(index)}
                                                className={`relative w-16 h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                                                    index === selectedImage 
                                                        ? 'ring-3 ring-red-500 ring-offset-2 shadow-lg scale-110' 
                                                        : 'hover:ring-2 hover:ring-red-300 hover:ring-offset-1 hover:scale-105 opacity-70 hover:opacity-100'
                                                }`}
                                            >
                                                <img 
                                                    src={image}
                                                    alt={`${product.name} - ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Enhanced Product Info */}
                        <div className="lg:col-span-3 p-6 lg:p-10 space-y-6">
                            {/* Category Badge */}
                            {product.category && (
                                <div className="inline-flex items-center">
                                    <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg backdrop-blur-sm">
                                        {product.category.name}
                                    </span>
                                </div>
                            )}

                            {/* Product Title */}
                            <div>
                                <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent leading-tight mb-4">
                                    {product.name}
                                </h1>
                            </div>

                            {/* Description */}
                            <p className="text-lg text-slate-700 leading-relaxed font-medium">
                                {product.description}
                            </p>

                            {/* Price Section */}
                            <div className="space-y-2">
                                {hasDiscount ? (
                                    <div className="space-y-1">
                                        <p className="text-lg text-slate-500 line-through font-medium">
                                            {formatPrice(originalPrice)} so'm
                                        </p>
                                        <div className="flex items-baseline space-x-3">
                                            <p className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                                                {formatPrice(discountPrice)} so'm
                                            </p>
                                            <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                                                -{Math.round(((originalPrice - discountPrice) / originalPrice) * 100)}%
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                                        {formatPrice(regularPrice)} so'm
                                    </p>
                                )}
                            </div>

                            {/* Stock Status */}
                            <div className="flex items-center space-x-3">
                                <div className={`relative w-3 h-3 rounded-full ${product.stock_quantity > 0 ? 'bg-green-500' : 'bg-red-500'}`}>
                                    {product.stock_quantity > 0 && (
                                        <div className="absolute inset-0 bg-green-400 rounded-full animate-ping"></div>
                                    )}
                                </div>
                                <span className={`text-lg font-semibold ${product.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {product.stock_quantity > 0 ? t('common.inStock') || 'Omborda mavjud' : t('common.outOfStock') || 'Omborda yo\'q'}
                                </span>
                            </div>

                            {/* CTA Button */}
                            <div className="pt-4">
                                <button 
                                    onClick={() => setShowOrderModal(true)}
                                    disabled={product.stock_quantity <= 0}
                                    className={`group relative w-full lg:w-auto px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform ${
                                        product.stock_quantity > 0
                                            ? 'bg-gradient-to-r from-red-600 via-red-700 to-red-600 bg-size-200 bg-pos-0 hover:bg-pos-100 text-white shadow-2xl hover:shadow-red-500/25 hover:scale-105 active:scale-95'
                                            : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white cursor-not-allowed'
                                    }`}
                                >
                                    <span className="relative z-10">
                                        {product.stock_quantity > 0 ? t('pages.products.addToCart') || 'Sotib Olish' : t('common.outOfStock')}
                                    </span>
                                    {product.stock_quantity > 0 && (
                                        <>
                                            <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                            <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 backdrop-blur-sm"></div>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                {product.features && (
                    <div className="mt-16">
                        <div className="bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 p-8 lg:p-12">
                            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-8">
                                {t('pages.products.features') || 'Mahsulot Xususiyatlari'}
                            </h2>
                            <div 
                                className="prose lg:prose-lg max-w-none text-slate-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: product.features }}
                            />
                        </div>
                    </div>
                )}

                {/* Enhanced Reviews Section */}
                <div className="mt-16">
                    <div className="bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 p-8 lg:p-12">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                                {t('pages.products.reviews') || 'Mijoz Sharhlari'} ({reviews.length})
                            </h2>
                            <button
                                onClick={() => setShowReviewModal(true)}
                                className="group relative px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-105 active:scale-95"
                            >
                                <span className="relative z-10">{t('pages.products.reviewModal.title') || 'Sharh qoldirish'}</span>
                                <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>
                        
                        {reviews.length > 0 ? (
                            <div className="grid gap-6">
                                {reviews.map(review => (
                                    <div key={review.id} className="group bg-gradient-to-r from-white/50 to-slate-50/50 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                                    {review.author_name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-800">{review.author_name}</p>
                                                    <p className="text-sm text-slate-500">{new Date(review.created_at).toLocaleDateString('uz-UZ')}</p>
                                                </div>
                                            </div>
                                            <div className="flex space-x-1">
                                                {[...Array(review.rating)].map((_, i) => (
                                                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                                                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                                    </svg>
                                                ))}
                                                {[...Array(5 - review.rating)].map((_, i) => (
                                                    <svg key={i} className="w-5 h-5 text-slate-300 fill-current" viewBox="0 0 24 24">
                                                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                                    </svg>
                                                ))}
                                            </div>
                                        </div>
                                        <p className="text-slate-700 leading-relaxed">{review.text}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                </div>
                                <p className="text-slate-600 font-medium">
                                    {t('pages.products.noReviews') || 'Bu mahsulot uchun hali sharh yo\'q. Birinchi sharhni siz qoldiring!'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Enhanced Other Products Section */}
                {otherProducts.length > 0 && (
                    <div className="mt-16">
                        <div className="bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 p-8 lg:p-12">
                            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-8">
                                {t('pages.products.otherProducts') || 'Boshqa Mahsulotlar'}
                            </h2>
                            {/* Fixed Grid Layout for Proper Card Display */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {otherProducts.slice(0, 3).map((p, index) => (
                                    <div 
                                        key={p.id} 
                                        className="group transform hover:scale-105 transition-transform duration-300 h-full"
                                        style={{ 
                                            animationDelay: `${index * 0.1}s`,
                                            animation: 'fadeInUp 0.6s ease-out forwards'
                                        }}
                                    >
                                        <ProductCard product={p} showButton={true} />
                                    </div>
                                ))}
                            </div>
                            {/* Show More Button if there are more products */}
                            {otherProducts.length > 3 && (
                                <div className="text-center mt-8">
                                    <Link 
                                        to="/products" 
                                        className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                    >
                                        <span className="mr-2">{t('pages.home.allProducts') || 'Barcha Mahsulotlar'}</span>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Container>

            {/* Enhanced Order Modal */}
            {showOrderModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md transform animate-fadeInScale">
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-6">
                            {t('pages.products.orderModal.title') || 'Sotib olish'}
                        </h2>
                        {orderMessage && (
                            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl mb-4">
                                {orderMessage}
                            </div>
                        )}
                        {orderError && (
                            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-4">
                                {orderError}
                            </div>
                        )}
                        <form onSubmit={handleOrderSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.orderModal.firstName') || 'Ism'}</label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={orderForm.first_name}
                                    onChange={handleOrderFormChange}
                                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.orderModal.lastName') || 'Familiya'}</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={orderForm.last_name}
                                    onChange={handleOrderFormChange}
                                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.orderModal.phone') || 'Telefon raqami'}</label>
                                <input
                                    type="text"
                                    name="phone_number"
                                    value={orderForm.phone_number}
                                    onChange={handleOrderFormChange}
                                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowOrderModal(false)}
                                    className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all duration-200"
                                >
                                    {t('pages.products.orderModal.cancel') || 'Bekor qilish'}
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-3 px-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
                                >
{t('pages.products.orderModal.submit') || 'Yuborish'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Enhanced Review Modal */}
            {showReviewModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-md transform animate-fadeInScale">
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-6">
                            {t('pages.products.reviewModal.title') || 'Sharh qoldirish'}
                        </h2>
                        {reviewMessage && (
                            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl mb-4">
                                {reviewMessage}
                            </div>
                        )}
                        {reviewError && (
                            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-4">
                                {reviewError}
                            </div>
                        )}
                        <form onSubmit={handleReviewSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.reviewModal.name') || 'Ismingiz'}</label>
                                <input
                                    type="text"
                                    name="author_name"
                                    value={reviewForm.author_name}
                                    onChange={handleReviewFormChange}
                                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.reviewModal.comment') || 'Sharhingiz'}</label>
                                <textarea
                                    name="text"
                                    value={reviewForm.text}
                                    onChange={handleReviewFormChange}
                                    rows="4"
                                    className="w-full px-4 py-3 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 resize-none"
                                    required
                                ></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pages.products.reviewModal.rating') || 'Reyting (1-5)'}</label>
                                <div className="flex space-x-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setReviewForm({...reviewForm, rating: star})}
                                            className={`p-1 transition-all duration-200 hover:scale-110 ${star <= reviewForm.rating ? 'text-yellow-400' : 'text-slate-300 hover:text-yellow-200'}`}
                                        >
                                            <svg className="w-8 h-8 fill-current" viewBox="0 0 24 24">
                                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowReviewModal(false)}
                                    className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all duration-200"
                                >
                                    {t('pages.products.reviewModal.cancel') || 'Bekor qilish'}
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105"
                                >
{t('pages.products.reviewModal.submit') || 'Sharh qoldirish'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* YouTube Video Modal */}
            <YouTubeModal 
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                videoUrl={product?.youtube_url}
                productName={product?.name}
            />

            <style jsx>{`
                @keyframes fadeInScale {
                    from {
                        opacity: 0;
                        transform: scale(0.9) translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                
                .animate-fadeInScale {
                    animation: fadeInScale 0.3s ease-out;
                }
                
                .bg-size-200 {
                    background-size: 200% 200%;
                }
                
                .bg-pos-0 {
                    background-position: 0% 50%;
                }
                
                .bg-pos-100 {
                    background-position: 100% 50%;
                }
                
                .animation-delay-150 {
                    animation-delay: 150ms;
                }
                
                .group:hover .group-hover\\:scale-105 {
                    transform: scale(1.05);
                }
            `}</style>
        </div>
    );
};

export default ProductDetail;
